import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import hexToRgba from 'hex-to-rgba';
// import { motion, AnimatePresence } from 'framer-motion';
import { Flex, FlexColumn } from '../../../Reusable/Container';
import { Text } from '../../../Reusable/Text';
import { LabeledInput } from '../../../Reusable/Input';
import { Button } from '../../../Reusable/Button';
import moment from 'moment';
import PDFDocument from './PDFDocument';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { CardContainer } from './Reusable/CardContainer';
import PayCard from './Reusable/PayCard';

// Styled components for the bill viewer panel
const ViewerPanelContainer = styled(FlexColumn)`
  width: 100%;
  height: 100%;
  position: relative;
  padding-left: 18%;
  padding-right: 18%;
  overflow-y: auto; /* Enable scrolling for the entire panel */
  ${props => props.payMode && `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  `}
`;

// PayBillCard with proper stickiness and positioning
const PayBillCard = styled(CardContainer)`
  padding: ${props => props.isExpanded ? '24px' : '16px 24px'};
  /* margin-bottom: 20px; */
  width: ${props => props.isExpanded ? '70%' : '100%'};
  min-width: ${props => props.isExpanded ? '750px' : 'auto'};
  max-width: ${props => props.isExpanded ? '1000px' : 'auto'};
  z-index: 10;
  overflow: visible;
  align-self: ${props => props.isExpanded ? 'center' : 'stretch'};
  position: sticky; 
  top: 0;
  background: ${props => props.theme.body};
  
  ${props => props.isExpanded && `
    margin-top: 0px;
  `}
`;

const PayCardHeader = styled(Flex)`
  cursor: pointer;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
`;

const PDFContainer = styled.div`
  width: 100%;
  position: relative;
  transition: opacity 0.3s ease;
  margin-top: ${props => props.isExpanded ? '40px' : '20px'};
`;

const ContentContainer = styled.div`
  width: 100%;
  margin-top: ${props => props.isVisible ? '0px' : '0'};
  height: ${props => props.isVisible ? 'auto' : '0'};
  overflow: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

class BillViewerPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            paymentAmount: this.props.bill?.amount || 0,
            pageNumber: 1,
            numPages: 1,
            documentLoaded: false,
            isScrollingDown: false,
            lastScrollTop: 0,
            isMouseOverPDF: false,
            panelScrollTop: 0,
            lastPanelScrollTop: 0
        };

        this.scrollTimeoutRef = null;
        this.pdfContainerRef = React.createRef();
        this.panelContainerRef = React.createRef();
        
        // Configurable scroll threshold (percentage of container height)
        this.scrollThresholdPercentage = this.props.scrollThresholdPercentage || 5; // Default 5%
    }

    componentDidMount() {
        // Add event listeners for mouse entering/leaving PDF container
        this.addPDFMouseEventListeners();
        // Add event listeners for panel scrolling
        this.addPanelScrollEventListeners();
    }

    componentDidUpdate(prevProps, prevState) {
        // Update payment amount when bill amount changes
        if (prevProps.bill?.amount !== this.props.bill?.amount && this.props.bill?.amount) {
            this.setState({ paymentAmount: this.props.bill.amount });
        }

        // Handle changes to the entire bill object
        if (prevProps.bill !== this.props.bill) {
            this.setState({ bill: this.props.bill });
        }

        // Handle changes to the vendor
        if (prevProps.vendor !== this.props.vendor) {
            this.setState({ vendor: this.props.vendor });
        }

        // Handle changes to billUrl
        if (prevProps.billUrl !== this.props.billUrl) {
            this.setState({ billUrl: this.props.billUrl });
        }

        // Handle changes to view/pay mode
        if (prevProps.payMode !== this.props.payMode) {
            this.setState({ 
                isScrollingDown: false,
                panelScrollTop: 0,
                lastPanelScrollTop: 0
            });
        }

        // Handle changes to scroll threshold
        if (prevProps.scrollThresholdPercentage !== this.props.scrollThresholdPercentage) {
            this.scrollThresholdPercentage = this.props.scrollThresholdPercentage || 5;
        }

        // Re-add event listeners when PDF container changes or document loads
        if (prevProps.billUrl !== this.props.billUrl || 
            prevState.documentLoaded !== this.state.documentLoaded) {
            this.addPDFMouseEventListeners();
        }

        // Re-add panel scroll listeners when payMode changes
        if (prevProps.payMode !== this.props.payMode) {
            this.addPanelScrollEventListeners();
        }

        // Notify parent component when isMouseOverPDF changes
        if (prevState.isMouseOverPDF !== this.state.isMouseOverPDF && this.props.onMouseOverPDFChange) {
            this.props.onMouseOverPDFChange(this.state.isMouseOverPDF);
        }
    }

    componentWillUnmount() {
        // Clean up event listeners
        this.removePDFMouseEventListeners();
        this.removePanelScrollEventListeners();
        
        // Clear any pending timeouts
        if (this.scrollTimeoutRef) {
            clearTimeout(this.scrollTimeoutRef);
        }
    }

    addPDFMouseEventListeners = () => {
        // First remove any existing listeners to prevent duplicates
        this.removePDFMouseEventListeners();
        
        // Add new listeners if PDF container is available
        if (this.pdfContainerRef.current) {
            this.pdfContainerRef.current.addEventListener('mouseenter', this.handleMouseEnterPDF);
            this.pdfContainerRef.current.addEventListener('mouseleave', this.handleMouseLeavePDF);
        }
    }

    removePDFMouseEventListeners = () => {
        if (this.pdfContainerRef.current) {
            this.pdfContainerRef.current.removeEventListener('mouseenter', this.handleMouseEnterPDF);
            this.pdfContainerRef.current.removeEventListener('mouseleave', this.handleMouseLeavePDF);
        }
    }

    addPanelScrollEventListeners = () => {
        // First remove any existing listeners to prevent duplicates
        this.removePanelScrollEventListeners();
        
        // Add new listeners if panel container is available
        if (this.panelContainerRef.current) {
            this.panelContainerRef.current.addEventListener('scroll', this.handlePanelScroll);
        }
    }

    removePanelScrollEventListeners = () => {
        if (this.panelContainerRef.current) {
            this.panelContainerRef.current.removeEventListener('scroll', this.handlePanelScroll);
        }
    }

    handleMouseEnterPDF = () => {
        this.setState({ isMouseOverPDF: true });
    }

    handleMouseLeavePDF = () => {
        this.setState({ isMouseOverPDF: false });
    }

    handlePanelScroll = (event) => {
        const { payMode, onPayCardToggle } = this.props;
        
        // Only handle scroll behavior when in payMode
        if (!payMode || !onPayCardToggle) {
            return;
        }

        const container = event.target;
        
        // Check if the scroll event is coming from the main panel container
        // and not from a child element (like dropdown, select, etc.)
        if (container !== this.panelContainerRef.current) {
            return; // Ignore scroll events from child elements
        }

        // Additional check: ignore scroll events from common scrollable UI elements
        const scrollableSelectors = [
            '.ant-select-dropdown',
            '.ant-dropdown',
            '.ant-menu',
            '.ant-cascader-menu',
            '.ant-picker-dropdown',
            '.ant-tooltip',
            '.ant-popover',
            '[class*="dropdown"]',
            '[class*="select"]',
            '[class*="menu"]',
            '[class*="popover"]'
        ];

        // Check if the event originated from within any scrollable UI component
        let targetElement = event.target;
        while (targetElement && targetElement !== this.panelContainerRef.current) {
            if (scrollableSelectors.some(selector => targetElement.matches && targetElement.matches(selector))) {
                return; // Ignore scroll from UI components
            }
            // Also check for custom class patterns that might indicate scrollable content
            if (targetElement.className && typeof targetElement.className === 'string') {
                const classList = targetElement.className.toLowerCase();
                if (classList.includes('dropdown') || 
                    classList.includes('select') || 
                    classList.includes('menu') ||
                    classList.includes('popover') ||
                    classList.includes('tooltip')) {
                    return;
                }
            }
            targetElement = targetElement.parentElement;
        }

        const currentScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        const scrollThreshold = (containerHeight * this.scrollThresholdPercentage) / 100;

        // Calculate scroll distance from the last recorded position
        const scrollDistance = Math.abs(currentScrollTop - this.state.lastPanelScrollTop);

        // If user has scrolled more than the threshold, toggle off payMode
        if (scrollDistance > scrollThreshold) {
            // Clear any existing timeout
            if (this.scrollTimeoutRef) {
                clearTimeout(this.scrollTimeoutRef);
            }

            // Add a small delay to prevent excessive toggling
            this.scrollTimeoutRef = setTimeout(() => {
                if (payMode) {
                    onPayCardToggle();
                }
            }, 100);

            // Update the last scroll position
            this.setState({ 
                lastPanelScrollTop: currentScrollTop,
                panelScrollTop: currentScrollTop
            });
        }
    }

    // Reset scroll tracking when payMode is enabled
    resetScrollTracking = () => {
        if (this.panelContainerRef.current) {
            const currentScrollTop = this.panelContainerRef.current.scrollTop;
            this.setState({
                lastPanelScrollTop: currentScrollTop,
                panelScrollTop: currentScrollTop
            });
        }
    }

    handlePaymentAmountChange = (value) => {
        this.setState({ paymentAmount: value });
    };

    handleAccountChange = (value) => {
        this.setState({ selectedAccount: value });
    };

    handleVendorAccountChange = (value) => {
        this.setState({ vendorAccount: value });
    };

    onDocumentLoadSuccess = ({ numPages }) => {
        this.setState({
            numPages,
            documentLoaded: true
        });
    };

    // Enhanced onPayCardToggle handler
    handlePayCardToggle = () => {
        const { onPayCardToggle } = this.props;
        
        if (onPayCardToggle) {
            onPayCardToggle();
            
            // Reset scroll tracking when toggling to payMode
            setTimeout(() => {
                this.resetScrollTracking();
            }, 100);
        }
    };

    render() {
        const {
            bill,
            vendor,
            billUrl,
            onPay,
            viewMode = true,
            payMode = false,
            loading,
            scrollThresholdPercentage = 5,
            billBalance,
            billAmtPaid
        } = this.props;

        // Bill and vendor data with defaults
        const billNumber = bill?.invoiceNumber;

        // Determine whether the bill is unpaid and if there's a partial payment
        const unpaid = ['Unpaid', 'Canceled'].includes(bill?.status) || bill?.balance > 0;

        // Animation variants for chevron
        const chevronVariants = {
            up: { rotate: 180 },
            down: { rotate: 0 }
        };

        return (
            <ViewerPanelContainer 
                ref={this.panelContainerRef}
                payMode={payMode}
            >
                {/* Pay Bill Card with stickiness */}
                {
                    bill?.id && unpaid &&
                    <PayBillCard
                        key={payMode}
                        isExpanded={payMode}
                        animate={{
                            scale: 1,
                            opacity: 1,
                        }}
                        transition={{ duration: 0.3 }}
                        gradientBorder={payMode}
                        showShadow
                    >
                        <PayCardHeader onClick={this.handlePayCardToggle}>
                            <Text weight="600" size="18px">
                                {this.props.billAmtPaid ? 'Make another payment' : 'Pay bill'}
                            </Text>
                            <ChevronIcon>
                                <div
                                    variants={chevronVariants}
                                    animate={payMode ? "up" : "down"}
                                    transition={{ duration: 0.3 }}
                                >
                                    {payMode ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </div>
                            </ChevronIcon>
                        </PayCardHeader>

                        {/* Content Container with CSS transitions */}
                        <ContentContainer isVisible={payMode}>
                            <PayCard
                                key={payMode}
                                bill={bill}
                                billAmtPaid={billAmtPaid}
                                billBalance={billBalance}
                                vendor={vendor}
                                onPay={this.props.onPay}
                                saveBill={this.props.saveBill}
                                getSchldPmts={this.props.getSchldPmts}
                            />
                        </ContentContainer>
                    </PayBillCard>
                }

                {/* PDF Viewer */}
                <PDFContainer 
                    ref={this.pdfContainerRef}
                    isExpanded={payMode}
                >
                    {billUrl ? (
                        <PDFDocument
                            key={payMode}
                            download
                            url={billUrl}
                            fileName={billNumber || "bill"}
                            onLoadSuccess={this.onDocumentLoadSuccess}
                            height={payMode ? 550 : undefined}
                            width="100%"
                            scrollable={viewMode}
                            payMode={payMode}
                            isExpanded={payMode}
                        />
                    ) : (
                        loading ?
                        <Flex
                            center
                            style={{
                                height: '600px',
                                width: '100%',
                                border: '1px dashed #ccc',
                                borderRadius: '8px',
                                background: '#f9f9f9'
                            }}
                        >
                            <Text light>{loading ? "Loading" : ""}</Text>
                        </Flex>
                        :
                        null
                    )}
                </PDFContainer>

                {/* Debug info for scroll threshold (remove in production) */}
                {/* {process.env.NODE_ENV === 'development' && (
                    <div style={{ 
                        position: 'fixed', 
                        top: 10, 
                        right: 10, 
                        background: 'rgba(0,0,0,0.7)', 
                        color: 'white', 
                        padding: '8px', 
                        fontSize: '12px',
                        borderRadius: '4px',
                        zIndex: 9999
                    }}>
                        Scroll Threshold: {scrollThresholdPercentage}%
                        <br />
                        Current Scroll: {this.state.panelScrollTop}px
                        <br />
                        Last Scroll: {this.state.lastPanelScrollTop}px
                    </div>
                )} */}
            </ViewerPanelContainer>
        );
    }
}

export default BillViewerPanel;